import React, { useState } from 'react'
import { Share2, Mail, MessageCircle, Copy, ExternalLink, X } from 'lucide-react'
import { createOrderTransfer, generateTransferLink, getTransferSharingText, validateTransferContact } from '../../lib/transfers'
import { formatPrice } from '../../lib/square'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface TransferOrderFormProps {
  order: any
  user: any
  onSuccess?: () => void
  onCancel?: () => void
}

const TransferOrderForm: React.FC<TransferOrderFormProps> = ({ order, user, onSuccess, onCancel }) => {
  const [step, setStep] = useState<'contact' | 'share'>('contact')
  const [contact, setContact] = useState('')
  const [personalMessage, setPersonalMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [transferLink, setTransferLink] = useState('')
  const [sharingText, setSharingText] = useState({ sms: '', email: '' })

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate contact
    const validation = validateTransferContact(contact)
    if (!validation.isValid) {
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="error"
          title="Invalid Contact"
          message={validation.error || 'Invalid contact information'}
          visible={t.visible}
        />
      ))
      return
    }

    setLoading(true)
    try {
      // Create transfer
      const transfer = await createOrderTransfer({
        orderId: order.id,
        fromUserId: user.id,
        toContact: contact.trim(),
        personalMessage
      })

      // Generate sharing info
      const link = generateTransferLink(transfer.token)
      const sharingTexts = getTransferSharingText(order, user, link)

      setTransferLink(link)
      setSharingText(sharingTexts)
      setStep('share')

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="success"
          title="Transfer Created"
          message="Your order transfer has been created"
          visible={t.visible}
        />
      ))

    } catch (error) {
      console.error('Transfer creation failed:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="error"
          title="Transfer Failed"
          message={error.message || 'Failed to create transfer'}
          visible={t.visible}
        />
      ))
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="success"
          title="Copied"
          message="Text has been copied to clipboard"
          visible={t.visible}
        />
      ))
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="success"
          title="Copied"
          message="Text has been copied to clipboard"
          visible={t.visible}
        />
      ))
    }
  }

  const shareViaSMS = () => {
    const encodedText = encodeURIComponent(sharingText.sms)
    window.open(`sms:${contact}?body=${encodedText}`, '_blank')
  }

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`SECRET Order Transfer: ${order.order_number}`)
    const body = encodeURIComponent(sharingText.email)
    window.open(`mailto:${contact}?subject=${subject}&body=${body}`, '_blank')
  }

  const openTransferLink = () => {
    window.open(transferLink, '_blank')
  }

  if (step === 'share') {
    return (
      <div className="bg-secret-bg-secondary rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center space-x-2">
            <Share2 className="text-secret-primary" size={24} />
            <span>Share Transfer Link</span>
          </h3>
          {onCancel && (
            <button onClick={onCancel} className="text-secret-text-secondary hover:text-white">
              <X size={20} />
            </button>
          )}
        </div>

        <div className="space-y-6">
          {/* Success Message */}
          <div className="bg-green-600/20 border border-green-600 rounded-lg p-4">
            <h4 className="font-medium text-green-400 mb-2">🎉 Transfer Created Successfully!</h4>
            <p className="text-green-200 text-sm">
              Your order has been prepared for transfer to <strong>{contact}</strong>. 
              The link expires in 24 hours.
            </p>
          </div>

          {/* Transfer Link */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Transfer Link</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={transferLink}
                readOnly
                className="secret-input flex-1 text-secret-text-secondary"
              />
              <button
                onClick={() => copyToClipboard(transferLink)}
                className="secret-btn-secondary flex items-center space-x-1"
              >
                <Copy size={16} />
                <span>Copy</span>
              </button>
              <button
                onClick={openTransferLink}
                className="secret-btn-secondary flex items-center space-x-1"
              >
                <ExternalLink size={16} />
                <span>Open</span>
              </button>
            </div>
          </div>

          {/* Quick Share Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={shareViaSMS}
              className="flex items-center justify-center space-x-2 p-4 bg-secret-border hover:bg-secret-primary/20 text-white rounded-lg transition-colors"
            >
              <MessageCircle size={20} />
              <span>Share via SMS</span>
            </button>
            <button
              onClick={shareViaEmail}
              className="flex items-center justify-center space-x-2 p-4 bg-secret-border hover:bg-secret-primary/20 text-white rounded-lg transition-colors"
            >
              <Mail size={20} />
              <span>Share via Email</span>
            </button>
          </div>

          {/* Sharing Preview */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">SMS Preview</label>
              <div className="bg-black border border-secret-border rounded-lg p-3">
                <p className="text-secret-text-secondary text-sm">{sharingText.sms}</p>
                <button
                  onClick={() => copyToClipboard(sharingText.sms)}
                  className="text-secret-primary hover:underline text-xs mt-1"
                >
                  Copy SMS text
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">Email Preview</label>
              <div className="bg-black border border-secret-border rounded-lg p-3">
                <p className="text-secret-text-secondary text-sm">{sharingText.email}</p>
                <button
                  onClick={() => copyToClipboard(sharingText.email)}
                  className="text-secret-primary hover:underline text-xs mt-1"
                >
                  Copy email text
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-secret-border">
            <button
              onClick={() => {
                onSuccess?.()
                onCancel?.()
              }}
              className="secret-btn-primary"
            >
              Done
            </button>
          </div>

          {/* Important Notes */}
          <div className="bg-secret-bg-tertiary rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Important Notes:</h4>
            <ul className="text-secret-text-secondary text-sm space-y-1">
              <li>• The transfer link expires in 24 hours</li>
              <li>• Once claimed, you cannot access this order</li>
              <li>• The recipient will need to create a SECRET account if they don't have one</li>
              <li>• Both parties will receive confirmation notifications</li>
            </ul>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-secret-bg-secondary rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center space-x-2">
          <Share2 className="text-secret-primary" size={24} />
          <span>Transfer Order</span>
        </h3>
        {onCancel && (
          <button onClick={onCancel} className="text-secret-text-secondary hover:text-white">
            <X size={20} />
          </button>
        )}
      </div>

      {/* Order Summary */}
      <div className="bg-black rounded-lg p-4 mb-6">
        <h4 className="font-medium text-white mb-3">Order Details</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-secret-text-secondary">Order Number:</span>
            <span className="text-white">{order.order_number}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-secret-text-secondary">Event:</span>
            <span className="text-white">{order.secret_events?.title}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-secret-text-secondary">Total:</span>
            <span className="text-secret-primary font-medium">{formatPrice(order.total_amount * 100)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-secret-text-secondary">Status:</span>
            <span className="text-white capitalize">{order.status}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleContactSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Recipient Email or Phone Number *
          </label>
          <input
            type="text"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="friend@example.com or +1 (555) 123-4567"
            className="secret-input"
            required
          />
          <p className="text-secret-text-tertiary text-xs mt-1">
            The person you're transferring to will receive a secure link
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Personal Message (Optional)
          </label>
          <textarea
            value={personalMessage}
            onChange={(e) => setPersonalMessage(e.target.value)}
            placeholder="Hey! I can't make it to the SECRET event, but I thought you'd love this order..."
            className="secret-input resize-y min-h-20"
            maxLength={200}
            rows={3}
          />
          <p className="text-secret-text-tertiary text-xs mt-1">
            {personalMessage.length}/200 characters
          </p>
        </div>

        {/* Security Info */}
        <div className="bg-secret-bg-tertiary rounded-lg p-4">
          <h4 className="font-medium text-white mb-2">🔒 Transfer Security</h4>
          <ul className="text-secret-text-secondary text-sm space-y-1">
            <li>• Transfer links expire automatically after 24 hours</li>
            <li>• Each link can only be used once</li>
            <li>• You can cancel the transfer before it's claimed</li>
            <li>• Both parties receive notifications</li>
          </ul>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-secret-border">
          {onCancel && (
            <button type="button" onClick={onCancel} className="secret-btn-secondary">
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="secret-btn-primary flex items-center space-x-2"
          >
            {loading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Share2 size={16} />
                <span>Create Transfer</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default TransferOrderForm